import {
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSText
} from "@wingspanhq/fe-component-library";
import { IRequirementDefinitionResponse } from "@wingspanhq/payments/dist/interfaces";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import React from "react";
import { ErrorContextKey } from "../../../services/platform";
import { DefaultActionDetails } from "../components/DefaultActionDetails";
import { useDeleteRequirementDefinition } from "../../../query/requirementDefinitions/mutation/useDeleteRequirementDefinition";

export interface DeleteRequirementModalProps {
  requirementDefinition: IRequirementDefinitionResponse;
  engagementLength: number;
  onClose: () => void;
  onSuccess: (requirement: IRequirementDefinitionResponse) => void;
}

export const DeleteRequirementModal: React.FC<DeleteRequirementModalProps> = ({
  requirementDefinition,
  engagementLength,
  onClose,
  onSuccess
}) => {
  const { openSnackbar } = useWSSnackbar();
  const [deleteRequirementDefinition, deleteRequirementDefinitionMeta] =
    useDeleteRequirementDefinition();

  const handleDelete = async () => {
    await deleteRequirementDefinition(
      requirementDefinition.requirementDefinitionId,
      {
        onSuccess: () => {
          openSnackbar({
            theme: "dark",
            type: "success",
            message: "Requirement deleted"
          });
          onClose();
          onSuccess(requirementDefinition);
        }
      }
    );
  };

  return (
    <>
      <WSText.ParagraphSm mb="L" weight="book">
        Are you sure you want to delete this requirement?
      </WSText.ParagraphSm>
      <DefaultActionDetails
        requirementDefinition={requirementDefinition}
        engagementLength={engagementLength}
      />

      <WSAlert
        my="2XL"
        theme="warning"
        icon="alert"
        size="M"
        title="Deleting requirement"
      >
        Deletion is a permanent action, it cannot be restored.
      </WSAlert>

      <WSErrorMessage
        error={deleteRequirementDefinitionMeta.error}
        mb="XL"
        contextKey={ErrorContextKey.Other}
      />

      <WSActions
        alignment="fill"
        sortButtons={false}
        buttons={[
          {
            label: "Cancel",
            onClick: onClose,
            kind: "Secondary"
          },
          {
            label: "Delete requirement",
            loading: deleteRequirementDefinitionMeta.isLoading,
            onAsyncClick: handleDelete,
            kind: "Primary",
            destructive: true
          }
        ]}
      />
    </>
  );
};

export const useDeleteRequirementModal = () => {
  return useWSModal(DeleteRequirementModal, {
    title: "Delete requirement",
    size: "M"
  });
};
